import React from 'react';
import { TableColumnResizing } from '@devexpress/dx-react-grid-material-ui';
import { TableStateContext } from '../TableStateContext';
import { isFillEnabled } from '../fillEnabledTables';
import { writeColumnWidths } from '../columnWidthStorage';

// Matches the `minWidth` the table's columnExtensions already advertise, so a
// drag can't shrink a column past the floor fill itself respects.
const MIN_COLUMN_WIDTH = 90;

export const ColumnResizingPlugin = () => {
  // @ts-expect-error TS(2339): Property 'state' does not exist on type 'unknown'.
  const { state, handlers, ...rest } = React.useContext(TableStateContext);
  const tableId = rest?.tableId;

  // This callback only fires for a real drag, which makes it the one honest
  // place to record a user-chosen layout — BaseTable restores it on the next
  // visit. Fill seeding goes through onSeedColumnWidths instead, so automatic
  // widths are never written here.
  const handleColumnWidthsChange = React.useCallback(
    (widths: any) => {
      writeColumnWidths(tableId, widths);
      handlers.onColumnWidthsChange(widths);
    },
    [tableId, handlers]
  );

  return (
    <ColumnSizingPluginBase
      columnWidths={state.columnWidths}
      onColumnWidthsChange={handleColumnWidthsChange}
      fill={isFillEnabled(tableId, state?.fillAvailableWidth)}
    />
  );
};

/**
 * Resize behaviour for a filled table.
 *
 * This used to be `nextColumn`, which keeps the table exactly full width by
 * trading width with the neighbouring column. In practice that made resizing
 * feel broken: a drag can only take whatever slack the neighbour has above
 * MIN_COLUMN_WIDTH, and on a table whose columns are already sized close to
 * that floor there is almost none. Measured on claim Documents, a 120px drag
 * moved the column by 25px (Name), 45px (Agency) and 5px (Download) — the
 * neighbour hit the floor immediately and the column simply stopped following
 * the pointer.
 *
 * `widget` mode moves the column 1:1 with the drag and grows the table. That
 * can put the table into horizontal scroll, but only because someone asked for
 * a column wider than the space available — which is ordinary table behaviour,
 * and far better than a handle that does not follow the mouse. Until the first
 * drag, fill still sizes the table to its container exactly.
 *
 * Non-fill tables keep DevExtreme's defaults untouched.
 */
export const getFillResizingProps = (fill: boolean) => {
  if (!fill) {
    return {};
  }
  return { resizingMode: 'widget', minColumnWidth: MIN_COLUMN_WIDTH };
};

const ColumnSizingPluginBase = ({
  columnWidths,
  onColumnWidthsChange,
  fill,
}: any) => {
  const fillResizingProps = getFillResizingProps(fill);

  return (
    <TableColumnResizing
      columnWidths={columnWidths}
      onColumnWidthsChange={onColumnWidthsChange}
      {...fillResizingProps}
    />
  );
};
